import { ReactNode } from 'react';

import { RequisitesForm } from 'store/models/requisites';

export enum RequisitesFormName {
    FinalAdvertiserRequisites = 'finalAdvertiserRequisites',
    AgencyRequisites = 'agencyRequisites',
    PrimaryAgencyRequisites = 'primaryAgencyRequisites',
}

export enum RequisitesFieldName {
    Consent = 'consent',
    IsAgency = 'isAgency',
    IsAgreementDirect = 'isAgreementDirect',
    FinalCounterpartyType = 'finalCounterpartyType',
}

export enum RequisiteName {
    LegalEntityType = 'legalEntityType',
    LegalName = 'legalName',
    Inn = 'inn',
    AgreementId = 'agreementId',
    AgreementDate = 'agreementDate',
    AgreementType = 'agreementType',
}

export enum FinalCounterpartyType {
    HH = 'HH',
    COZHH = 'COZ_HH',
}

// todo: Physical, ForeignLegal, ForeignPhysical пока убираем из LEGAL_ENTITY.values, чтобы их нельзя было выбрать.
//  Когда/если сможем отправлять в ОРД физиков и иностранцев, нужно будет вернуть
export enum RequisiteLegalEntityTypeValue {
    Legal = 'LEGAL',
    Ip = 'IP',
    Physical = 'PHYSICAL',
    ForeignLegal = 'FOREIGN_LEGAL',
    ForeignPhysical = 'FOREIGN_PHYSICAL',
}

export enum RequisiteRole {
    FinalAdvertiser = 'FINAL_ADVERTISER',
    Agency = 'AGENCY',
}

export enum RequisiteAgreementType {
    Direct = 'DIRECT',
    NotDirect = 'NOT_DIRECT',
}

export enum RequisitesAgreementType {
    Service = 'SERVICE',
    Mediation = 'MEDIATION',
}

export enum RequisiteError {
    Empty = 'EMPTY_FIELD',
    NotDigit = 'NOT_A_DIGIT',
    InvalidLegalNameLength = 'INVALID_LEGAL_NAME_LENGTH',
    InvalidAgreementIdLength = 'INVALID_AGREEMENT_ID_LENGTH',
    InvalidIpCharacters = 'INVALID_IP_CHARACTERS',
    InvalidLegalCharacters = 'INVALID_LEGAL_CHARACTERS',
    InvalidAgreementIdCharacters = 'INVALID_AGREEMENT_ID_CHARACTERS',
    SymbolsOccurTwiceInARowOrInTheStrinfEdges = 'SYMBOLS_OCCUR_TWICE_IN_A_ROW_OR_IN_THE_STRING_EDGES',
    SpecialCaractersOnly = 'SPECIAL_CHARACTERS_ONLY',
    InvalidLegalInnLength = 'INVALID_LEGAL_INN_LENGTH',
    InvalidPhysicalInnLength = 'INVALID_PHYSICAL_INN_LENGTH',
    LegalEntityTypeNotSelected = 'LEGAL_ENTITY_TYPE_NOT_SELECTED',
    LegalEntityTypeCanNotBeChanged = 'LEGAL_ENTITY_TYPE_CAN_NOT_BE_CHANGED',
    LegalEntityTypeNotValid = 'INVALID_LEGAL_ENTITY_TYPE',
    ConsentNotAccepted = 'CONSENT_NOT_ACCEPTED',
    InvalidInnFormat = 'INVALID_INN_FORMAT',
    IsAgencyNotSelected = 'IS_AGENCY_NOT_SELECTED',
    IsAgreementDirectNotSelected = 'IS_AGREEMENT_DIRECT_NOT_SELECTED',
    AgencyCanNotBeDeleted = 'AGENCY_CAN_NOT_BE_DELETED',
    PrimaryAgencyCanNotBeDeleted = 'PRIMARY_AGENCY_CAN_NOT_BE_DELETED',
    IsAgencyShouldBeTrue = 'IS_AGENCY_SHOULD_BE_TRUE',
    AgencyRequisitesIsNull = 'AGENCY_REQUISITES_IS_NULL',
    PrimaryAgencyRequisitesIsNull = 'PRIMARY_AGENCY_REQUISITES_IS_NULL',
    EqualityInn = 'EQUALITY_INN',
    CounterparyNotSelected = 'COUNTERPARTY_NOT_SELECTED',
    AgreementTypeNotSelected = 'AGREEMENT_TYPE_NOT_SELECTED',
}

export enum RequisiteErrorType {
    COMMON = 'COMMON',
}

export enum FieldType {
    Select = 'SELECT',
    Input = 'INPUT',
    Checkbox = 'CHECKBOX',
    Calendar = 'CALENDAR',
    Radio = 'RADIO',
}

type TipByAgreementType = Record<RequisiteAgreementType, string>;
type TipByRole = Record<string, string | null | TipByAgreementType>;
type TipConfig = Partial<Record<RequisitesFormName, TipByRole>>;

const AGREEMENT_ID_TIPS: TipConfig = {
    [RequisitesFormName.FinalAdvertiserRequisites]: {
        [RequisiteRole.FinalAdvertiser]:
            'RequisitesForm.finalAdvertiserRequisites.field.agreementId.tip.finalAdvertiser',
        [RequisiteRole.Agency]: {
            [RequisiteAgreementType.Direct]:
                'RequisitesForm.finalAdvertiserRequisites.field.agreementId.tip.agency.direct',
            [RequisiteAgreementType.NotDirect]:
                'RequisitesForm.finalAdvertiserRequisites.field.agreementId.tip.agency.notDirect',
        },
    },
    [RequisitesFormName.AgencyRequisites]: {
        [RequisiteRole.FinalAdvertiser]: null,
        [RequisiteRole.Agency]: 'RequisitesForm.agencyRequisites.field.agreementId.tip.agency',
    },
};

const getAgreementIdTip = (
    requisiteFormName: RequisitesFormName,
    { isAgency, isAgreementDirect }: RequisitesForm
): string | null => {
    const tipsByRole: TipByRole | undefined = AGREEMENT_ID_TIPS[requisiteFormName];

    if (tipsByRole && isAgency) {
        const tip = tipsByRole[isAgency];
        if (isAgency === RequisiteRole.Agency && typeof tip === 'object' && isAgreementDirect) {
            return tip?.[isAgreementDirect] ?? null;
        }
        return typeof tip === 'string' ? tip : null;
    }
    return null;
};

export interface FormConfigItem {
    fieldName: RequisiteName;
    fieldType: FieldType;
    isShort?: boolean;
    values?: string[];
    labelValues?: Record<string, string>;
    label?: string;
    placeholder?: string;
    error?: Partial<Record<RequisiteError, string | null>>;
    getTip?: (requisites: RequisitesForm) => string | null;
    isNumericInput?: boolean;
    dataQa: string;
}

export type RequisitesFieldConfig = Omit<FormConfigItem, 'fieldName'> & {
    fieldName: RequisitesFieldName;
    title?: string;
    subtitle?: string;
    label?: string;
    tooltipContent?: ReactNode;
    dataQa: string;
};

const LEGAL_ENTITY: FormConfigItem = {
    fieldName: RequisiteName.LegalEntityType,
    fieldType: FieldType.Select,
    label: 'RequisitesForm.field.legalEntityType',
    values: [RequisiteLegalEntityTypeValue.Legal, RequisiteLegalEntityTypeValue.Ip],
    labelValues: {
        [RequisiteLegalEntityTypeValue.Legal]: 'RequisitesForm.field.legalEntityType.legal',
        [RequisiteLegalEntityTypeValue.Ip]: 'RequisitesForm.field.legalEntityType.ip',
        [RequisiteLegalEntityTypeValue.Physical]: 'RequisitesForm.field.legalEntityType.physical',
        [RequisiteLegalEntityTypeValue.ForeignLegal]: 'RequisitesForm.field.legalEntityType.foreignLegal',
        [RequisiteLegalEntityTypeValue.ForeignPhysical]: 'RequisitesForm.field.legalEntityType.foreignPhysical',
    },
    placeholder: 'RequisitesForm.field.legalEntityType.placeholder',
    error: {
        [RequisiteError.LegalEntityTypeNotSelected]: 'RequisitesForm.field.error.legalEntityType.empty',
        [RequisiteError.LegalEntityTypeCanNotBeChanged]: 'RequisitesForm.field.error.legalEntityType.canNotBeChanged',
        [RequisiteError.LegalEntityTypeNotValid]: 'RequisitesForm.field.error.legalEntityType.notValid',
    },
    dataQa: 'legal-entity-type',
};

const LEGAL_NAME: FormConfigItem = {
    fieldName: RequisiteName.LegalName,
    fieldType: FieldType.Input,
    label: 'RequisitesForm.field.legalName',
    placeholder: 'RequisitesForm.field.legalName.placeholder',
    error: {
        [RequisiteError.Empty]: 'RequisitesForm.field.error.empty',
        [RequisiteError.InvalidLegalNameLength]: 'RequisitesForm.field.error.legalName.InvalidLegalLength',
        [RequisiteError.InvalidIpCharacters]: 'RequisitesForm.field.error.legalName.InvalidIpCharacters',
        [RequisiteError.InvalidLegalCharacters]: 'RequisitesForm.field.error.legalName.InvalidLegalCharacters',
        [RequisiteError.SymbolsOccurTwiceInARowOrInTheStrinfEdges]:
            'RequisitesForm.field.error.legalName.SymbolsOccurTwiceInARowOrInTheStrinfEdges',
        [RequisiteError.SpecialCaractersOnly]: 'RequisitesForm.field.error.legalName.SpecialCaractersOnly',
    },
    dataQa: 'legal-name',
};

const INN: FormConfigItem = {
    fieldName: RequisiteName.Inn,
    fieldType: FieldType.Input,
    isNumericInput: true,
    isShort: false,
    label: 'RequisitesForm.field.inn',
    placeholder: 'RequisitesForm.field.number.placeholder',
    error: {
        [RequisiteError.Empty]: 'RequisitesForm.field.error.empty',
        [RequisiteError.NotDigit]: 'RequisitesForm.field.error.notDigit',
        [RequisiteError.InvalidLegalInnLength]: 'RequisitesForm.field.error.inn.invalidLegalInnLength',
        [RequisiteError.InvalidPhysicalInnLength]: 'RequisitesForm.field.error.inn.invalidPhysicalInnLength',
        [RequisiteError.InvalidInnFormat]: 'RequisitesForm.field.error.inn.invalidInnFormat',
        [RequisiteError.EqualityInn]: 'RequisitesForm.field.error.inn.equalityInn',
    },
    dataQa: 'inn',
};

const AGREEMENT_ID: FormConfigItem = {
    fieldName: RequisiteName.AgreementId,
    fieldType: FieldType.Input,
    isShort: true,
    label: 'RequisitesForm.field.agreementId',
    placeholder: 'RequisitesForm.field.number.placeholder',
    error: {
        [RequisiteError.Empty]: 'RequisitesForm.field.error.empty',
        [RequisiteError.InvalidAgreementIdLength]: 'RequisitesForm.field.error.agreementId.invalidAgreementIdLength',
        [RequisiteError.InvalidAgreementIdCharacters]:
            'RequisitesForm.field.error.agreementId.invalidAgreementIdCharacters',
    },
    dataQa: 'agreement-id',
};

const AGREEMENT_DATE: FormConfigItem = {
    fieldName: RequisiteName.AgreementDate,
    fieldType: FieldType.Calendar,
    isShort: true,
    label: 'RequisitesForm.field.agreementDate',
    placeholder: 'RequisitesForm.field.date.placeholder',
    error: {
        [RequisiteError.Empty]: 'RequisitesForm.field.error.empty',
    },
    dataQa: 'agreement-date',
};

export const ROLE: RequisitesFieldConfig = {
    label: 'RequisitesForm.field.role.label',
    fieldName: RequisitesFieldName.IsAgency,
    fieldType: FieldType.Radio,
    values: [RequisiteRole.FinalAdvertiser, RequisiteRole.Agency],
    labelValues: {
        [RequisiteRole.FinalAdvertiser]: 'RequisitesForm.field.role.finalAdvertiser',
        [RequisiteRole.Agency]: 'RequisitesForm.field.role.agency',
    },
    error: {
        [RequisiteError.IsAgencyNotSelected]: 'RequisitesForm.field.role.error.empty',
        [RequisiteError.AgencyCanNotBeDeleted]: 'RequisitesForm.field.role.error.canNotChange',
        [RequisiteError.IsAgencyShouldBeTrue]: 'RequisitesForm.field.role.error.shouldBeTrue',
    },
    dataQa: 'role',
};

export const AGREEMENT_TYPE: RequisitesFieldConfig = {
    label: 'RequisitesForm.field.agreementType.label',
    fieldName: RequisitesFieldName.IsAgreementDirect,
    fieldType: FieldType.Radio,
    values: [RequisiteAgreementType.Direct, RequisiteAgreementType.NotDirect],
    labelValues: {
        [RequisiteAgreementType.Direct]: 'RequisitesForm.field.agreementType.direct',
        [RequisiteAgreementType.NotDirect]: 'RequisitesForm.field.agreementType.notDirect',
    },
    error: {
        [RequisiteError.IsAgreementDirectNotSelected]: 'RequisitesForm.field.agreementType.error.empty',
        [RequisiteError.PrimaryAgencyCanNotBeDeleted]: 'RequisitesForm.field.agreementType.error.canNotChange',
    },
    dataQa: 'agreement-type',
};

export const FINAL_COUNTERPARTY_TYPE: RequisitesFieldConfig = {
    title: 'RequisitesForm.field.finalCounterpartyType.title',
    subtitle: 'RequisitesForm.finalCounterpartyType.subtitle',
    fieldName: RequisitesFieldName.FinalCounterpartyType,
    fieldType: FieldType.Radio,
    values: [FinalCounterpartyType.COZHH, FinalCounterpartyType.HH],
    labelValues: {
        [FinalCounterpartyType.HH]: 'RequisitesForm.field.finalCounterpartyType.hh',
        [FinalCounterpartyType.COZHH]: 'RequisitesForm.field.finalCounterpartyType.cozhh',
    },
    error: {
        [RequisiteError.CounterparyNotSelected]: 'RequisitesForm.field.finalCounterpartyType.error.empty',
    },
    dataQa: 'final-counterparty-type',
};

const AGENCY_AGREEMENT_TYPE: FormConfigItem = {
    fieldName: RequisiteName.AgreementType,
    fieldType: FieldType.Select,
    label: 'RequisitesForm.field.agreementType',
    values: [RequisitesAgreementType.Service, RequisitesAgreementType.Mediation],
    labelValues: {
        [RequisitesAgreementType.Service]: 'RequisitesForm.field.agreementType.service',
        [RequisitesAgreementType.Mediation]: 'RequisitesForm.field.agreementType.mediation',
    },
    placeholder: 'RequisitesForm.field.agreementType.placeholder',
    error: {
        [RequisiteError.AgreementTypeNotSelected]: 'RequisitesForm.field.error.agreementType.empty',
    },
    dataQa: 'agreement-type',
};

export const FINAL_ADVERTISER_FORM_CONFIG: FormConfigItem[] = [
    LEGAL_ENTITY,
    LEGAL_NAME,
    INN,
    {
        ...AGREEMENT_ID,
        getTip: (requisites: RequisitesForm) =>
            getAgreementIdTip(RequisitesFormName.FinalAdvertiserRequisites, requisites),
    },
    AGREEMENT_DATE,
];

export const FINAL_ADVERTISER_FORM_CONFIG_WITH_AGREEMENT_TYPE: FormConfigItem[] = [
    LEGAL_ENTITY,
    LEGAL_NAME,
    INN,
    AGENCY_AGREEMENT_TYPE,
    {
        ...AGREEMENT_ID,
        getTip: (requisites: RequisitesForm) =>
            getAgreementIdTip(RequisitesFormName.FinalAdvertiserRequisites, requisites),
    },
    AGREEMENT_DATE,
];

export const AGENCY_FORM_CONFIG: FormConfigItem[] = [
    LEGAL_ENTITY,
    LEGAL_NAME,
    INN,
    {
        ...AGREEMENT_ID,
        getTip: (requisites: RequisitesForm) => getAgreementIdTip(RequisitesFormName.AgencyRequisites, requisites),
    },
    AGREEMENT_DATE,
];

export const PRIMARY_AGENCY_FORM_CONFIG: FormConfigItem[] = [LEGAL_ENTITY, LEGAL_NAME, INN];

export const CONSENT_CONFIG_ERROR: Partial<Record<RequisiteError, string | null>> = {
    [RequisiteError.ConsentNotAccepted]: 'RequisitesForm.field.consent.error.empty',
};

export const COMMON_CONFIG_ERROR: Partial<Record<RequisiteError, string | null>> = {
    [RequisiteError.AgencyRequisitesIsNull]: 'RequisitesForm.common.error.notAgencyRequisites',
    [RequisiteError.PrimaryAgencyRequisitesIsNull]: 'RequisitesForm.common.error.notPrimaryAgencyRequisites',
};

export const DISABLED_REQUISITES_AFTER_FILLED: Array<RequisitesFieldName | RequisiteName> = [
    RequisiteName.LegalEntityType,
    RequisitesFieldName.IsAgency,
    RequisitesFieldName.IsAgreementDirect,
];
